import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { BarChart3, Loader2, ExternalLink, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import SEOHead from '../components/SEOHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function PlatformPartnerStats() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const token = params.get('token');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Missing access token. Use the link emailed to you on approval.');
      setLoading(false);
      return;
    }
    axios.get(`${API_URL}/api/platform-partners/${slug}/stats`, { params: { token } })
      .then((r) => setData(r.data))
      .catch((err) => setError(err.response?.data?.detail || 'Failed to load stats'))
      .finally(() => setLoading(false));
  }, [slug, token]);

  const copyUtm = async () => {
    if (!data?.partner?.utm_url) return;
    try {
      await navigator.clipboard.writeText(data.partner.utm_url);
      setCopied(true);
      toast.success('UTM URL copied');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-white/60">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-black text-white" data-testid="stats-error">
        <SEOHead title="Stats access denied — theailedger.ai" />
        <div className="max-w-xl mx-auto px-6 py-24 text-center">
          <h1 className="text-2xl font-bold mb-3">Access denied</h1>
          <p className="text-white/60 mb-6">{error}</p>
          <Link to="/" className="text-[#00F0FF] hover:underline">← theailedger.ai</Link>
        </div>
      </div>
    );
  }

  const max = Math.max(1, ...(data?.daily_clicks || []).map((d) => d.clicks));

  return (
    <div className="min-h-screen bg-black text-white" data-testid="stats-page">
      <SEOHead title={`${data.partner.name} — Partner stats`} />
      <header className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {data.partner.logo_url ? (
              <img src={data.partner.logo_url} alt="" className="h-9 w-9 rounded-md object-contain bg-white/5" />
            ) : (
              <div className="h-9 w-9 rounded-md bg-[#00F0FF]/10 flex items-center justify-center">
                <BarChart3 className="h-4 w-4 text-[#00F0FF]" />
              </div>
            )}
            <div>
              <h1 className="text-lg font-bold leading-tight">{data.partner.name}</h1>
              <p className="text-xs text-white/50 uppercase tracking-wider font-semibold">
                Partner stats · {data.partner.tier} {data.partner.plan ? `· ${data.partner.plan.replace(/_/g, ' ')}` : ''}
              </p>
            </div>
          </div>
          <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${
            data.partner.status === 'active'
              ? 'bg-[#00FF94]/15 text-[#00FF94] border border-[#00FF94]/30'
              : 'bg-yellow-500/15 text-yellow-300 border border-yellow-500/30'
          }`}>
            {data.partner.status}
          </span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10 space-y-8">
        {/* Headline KPIs */}
        <section className="grid sm:grid-cols-2 gap-4">
          <Kpi label="Clicks (all time)" value={data.totals.clicks_all_time} testid="kpi-clicks" />
          <Kpi label="Signups (postback)" value={data.totals.signups_all_time}
            note={data.partner.postback_url ? null : 'Postback not configured'} testid="kpi-signups" />
        </section>

        {/* Sparkline */}
        <section className="rounded-xl border border-white/10 bg-white/[0.03] p-6">
          <h2 className="text-sm font-bold uppercase tracking-wider text-white/60 mb-4">
            Daily clicks · last 30 days
          </h2>
          {(data.daily_clicks || []).length === 0 ? (
            <p className="text-white/40 text-sm text-center py-8">No clicks yet — your placement just went live.</p>
          ) : (
            <div className="flex items-end gap-1 h-32" data-testid="stats-sparkline">
              {data.daily_clicks.map((d) => (
                <div key={d.date} className="flex-1 group relative">
                  <div className="bg-[#00F0FF]/30 hover:bg-[#00F0FF] transition rounded-sm"
                    style={{ height: `${(d.clicks / max) * 100}%`, minHeight: '2px' }} />
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block bg-black/90 text-xs px-2 py-1 rounded border border-white/20 whitespace-nowrap">
                    {d.date} · {d.clicks}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Top referrers */}
        <section className="rounded-xl border border-white/10 bg-white/[0.03] p-6">
          <h2 className="text-sm font-bold uppercase tracking-wider text-white/60 mb-4">
            Top referrers · last 30 days
          </h2>
          {(data.top_referrers || []).length === 0 ? (
            <p className="text-white/40 text-sm">No referrer data yet.</p>
          ) : (
            <ul className="space-y-2 text-sm" data-testid="stats-referrers">
              {data.top_referrers.map((r, i) => (
                <li key={i} className="flex items-center justify-between gap-3 py-1">
                  <span className="text-white/80 truncate font-mono text-xs">{r.referrer}</span>
                  <span className="text-[#00F0FF] font-semibold">{r.clicks}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Tracking URL */}
        <section className="rounded-xl border border-white/10 bg-white/[0.03] p-6">
          <h2 className="text-sm font-bold uppercase tracking-wider text-white/60 mb-1">
            Your tracked URL
          </h2>
          <p className="text-xs text-white/50 mb-3">This is the URL theailedger.ai uses when buyers click your card. UTMs included.</p>
          {data.partner.utm_url ? (
            <div className="flex items-center gap-2 bg-black/40 border border-white/10 rounded-lg p-3 font-mono text-xs">
              <span className="text-white/80 truncate flex-1">{data.partner.utm_url}</span>
              <button onClick={copyUtm} data-testid="stats-copy-utm-btn"
                className="text-white/60 hover:text-white inline-flex items-center gap-1 text-xs">
                {copied ? <><Check className="h-3 w-3 text-[#00FF94]"/>copied</> : <><Copy className="h-3 w-3"/>copy</>}
              </button>
              <a href={data.partner.utm_url} target="_blank" rel="noopener noreferrer"
                className="text-white/60 hover:text-white inline-flex items-center gap-1 text-xs">
                <ExternalLink className="h-3 w-3"/>open
              </a>
            </div>
          ) : (
            <p className="text-white/40 text-sm">UTM URL pending approval.</p>
          )}
        </section>
      </main>

      <footer className="max-w-5xl mx-auto px-6 py-8 text-xs text-white/40">
        Bookmark this page · token never expires · share only with internal team.
      </footer>
    </div>
  );
}

function Kpi({ label, value, note, testid }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-6" data-testid={testid}>
      <p className="text-xs uppercase tracking-wider text-white/50 font-semibold mb-2">{label}</p>
      <p className="text-3xl font-bold text-white">{(value ?? 0).toLocaleString()}</p>
      {note && <p className="text-xs text-white/40 mt-1">{note}</p>}
    </div>
  );
}

export default PlatformPartnerStats;
